// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateXCheckoutState from "@werner/App/Handlers/Checkout/UpdateLoacalState";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { isRestrictedToShipwhenComplete } from "@werner/App/Store/Selectors/GeneralSelectors";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    CHECKOUT_WHEN_TO_SHIP,
    nextStepHandler,
    previousStepHandler,
} from "@werner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@werner/App/Widgets/CheckoutStepper/StepperContext";
import { productAddToCartButtonStyles } from "@werner/Overrides/Components/ProductAddToCartButton";
import ChevronLeftSolid from "@werner/XDL/Icons/ChevronLeftSolid";
import {
    color,
    createLinkIconStyles,
    doScroll,
    modalButtonsActionStyles,
    xScreen,
    xValidateOnSubmit,
} from "@werner/XDL/Utilities";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapDispatchToProps = {
    updateCart: makeHandlerChainAwaitable(updateCart),
    updateXCheckoutState,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutStepperActionsStyles {
    container?: InjectableCss;
    prev?: LinkPresentationProps;
    next?: ButtonPresentationProps;
}

export const styles: CheckoutStepperActionsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            margin-top: 20px;
            gap: 24px;
        `,
    },
    prev: {
        ...createLinkIconStyles(ChevronLeftSolid, {
            color: "secondary",
            typographyProps: {
                size: 16,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 30px;
                `,
            },
            icon: {
                iconProps: {
                    size: 8,
                },
            },
        }),
    },
    next: {
        variant: "primary",
        css: css`
            width: 100%;
        `,
    },
};

const CheckoutStepperActions: React.FC<Props> = props => {
    const { currentStep, steps, setCurrentStep } = useContext(StepperContext);
    const history = useHistory();

    const {
        cartPage,
        checkoutShippingPage,
        isLoading,
        cart,
        showPlaceOrderButton,
        showSubmitForApprovalOrder,
        poNumber,
        isShippingPageLoading,
        isUseOneTimeAddress,
        isPickup,
        restrictedToShipWhenComplete,
    } = useAppSelector(state => {
        const cartId = state.pages.checkoutShipping.cartId || state.pages.checkoutReviewAndSubmit.cartId;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        const isPickup = cartState.value?.fulfillmentMethod === FulfillmentMethod.PickUp;
        return {
            checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
            cartPage: getPageLinkByPageType(state, "CartPage"),
            isLoading: cartState.isLoading,
            cart: cartState.value,
            showPlaceOrderButton: canPlaceOrder(cartState.value),
            showSubmitForApprovalOrder: canSubmitForApprovalOrder(getCurrentCartState(state).value),
            requestedPickupDate: state.pages.checkoutReviewAndSubmit.requestedPickupDate,
            requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
            fulfillmentMethod: cartState.value?.fulfillmentMethod,
            poNumber: (state as CustomAppState).xCheckout?.payment.poNumber,
            isShippingPageLoading: state.pages.checkoutShipping.isUpdatingCart,
            isUseOneTimeAddress: (state as CustomAppState).xCheckout?.addresses.useOneTimeAddress,
            restrictedToShipWhenComplete: isRestrictedToShipwhenComplete(state),
            isPickup,
        };
    });

    useEffect(() => {
        props.updateXCheckoutState({
            shipping: {
                ...(isPickup ? { pickupNotes: cart?.notes } : { shippingNotes: cart?.notes }),
                attentionTo: cart?.properties.attentionTo,
                contactPhoneNumber: cart?.properties.contactPhoneNumber,
                shippingMethod: restrictedToShipWhenComplete
                    ? CHECKOUT_WHEN_TO_SHIP.ShipWhenComplete
                    : cart?.properties.shippingMethod,
            },
        });
    }, [cart]);

    const goToNextStep = async (nextStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.SHIPPING && !isPickup) {
            if (xValidateOnSubmit("siteContactPhoneNumber").length > 0) {
                return;
            }
            await props.updateCart({ fullExpand: true });
        }

        if (currentStep !== CHECKOUT_STEPS.ADDRESS) {
            setCurrentStep(nextStep.index);
        }

        doScroll(true);
    };

    const goToPrevStep = (prevStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(cartPage?.url as string);
        } else if (prevStep.index === CHECKOUT_STEPS.ADDRESS) {
            history.push(checkoutShippingPage?.url as string);
        } else {
            setCurrentStep(prevStep.index);
        }

        doScroll(true);
    };

    const prevStep = previousStepHandler(steps, currentStep);
    const nextStep = nextStepHandler(steps, currentStep);

    const isNextButtonDisabled = () => {
        if (currentStep < 0) {
            return true;
        }
        if (isLoading) {
            return true;
        }

        if (currentStep === CHECKOUT_STEPS.PAYMENT && cart?.requiresPoNumber && cart?.showPoNumber && !poNumber) {
            return true;
        }
        if (currentStep === CHECKOUT_STEPS.PAYMENT && !cart?.paymentMethod && !cart?.requiresApproval) {
            return true;
        }
        if (currentStep === CHECKOUT_STEPS.ADDRESS && isShippingPageLoading) {
            return true;
        }
        if (currentStep === CHECKOUT_STEPS.ADDRESS && isUseOneTimeAddress && !cart?.properties.oneTimeShipToAccount) {
            return true;
        }
        return false;
    };

    return (
        <StyledWrapper {...styles.container}>
            {currentStep !== CHECKOUT_STEPS.REVIEW && (
                <Button
                    {...styles.next}
                    onClick={() => goToNextStep(nextStep)}
                    disabled={isNextButtonDisabled()}
                    data-test-selector={`checkout_${nextStep.name || nextStep.displayName}`}
                    type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
                    aria-label={translate("Continue to {0}", nextStep.displayName || nextStep.name)}
                >
                    {translate("Next: {0}", nextStep.displayName || nextStep.name)}
                </Button>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showPlaceOrderButton && (
                <PlaceOrderButton styles={styles.next} />
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showSubmitForApprovalOrder && (
                <SubmitForApprovalButton extendedStyles={styles.next} />
            )}
            {currentStep !== CHECKOUT_STEPS.ADDRESS && (
                <Link
                    {...styles.prev}
                    onClick={() => goToPrevStep(prevStep)}
                    disabled={isLoading}
                    type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
                    data-test-selector={`checkout_${prevStep.name || prevStep.displayName}`}
                    aria-label="Back"
                >
                    {prevStep.name}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CheckoutStepperActions),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper Actions",
    },
};

export default widgetModule;
